import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import HandoffModal from "../../components/admin/HandoffModal";
import KycSlaCountdown from "../../components/admin/KycSlaCountdown";
import PassportRedactor from "../../components/admin/PassportRedactor";
import AdminSandboxKycCard from "../../components/admin/AdminSandboxKycCard";
import AdminApprovalBadge from "../../components/common/AdminApprovalBadge";
import WorkloadChart from "../../components/admin/kyc/WorkloadChart";
import WorkloadPanel from "../../components/admin/kyc/WorkloadPanel";
import KycThreadCard from "../../components/admin/kyc/KycThreadCard";
import PdfPreviewModal from "../../components/admin/kyc/PdfPreviewModal";
import { Card, Pill, Button, TextArea } from "../../components/common/Primitives";
import ConfirmReasonDialog from "../../components/common/ConfirmReasonDialog";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { formatDateTime } from "../../lib/datetime";
import {
  ShieldCheck, FileText, CheckCircle2, XCircle, MailQuestion,
  Building2, ArrowRight, Clock, AlertTriangle, Eye, Download,
  MessageCircle, EyeOff,
} from "lucide-react";

const CATEGORY_LABEL = {
  pan_card: "PAN Card",
  cin_certificate: "Incorporation / CIN",
  bank_statement: "Bank Statement",
  founder_id: "Founder ID",
  aadhaar: "Aadhaar",
  gstin: "GSTIN",
  moa: "MoA / AoA",
};
const humanize = (c) =>
  CATEGORY_LABEL[c] || String(c || "").replace(/_/g, " ").replace(/\b\w/g, (x) => x.toUpperCase());

const STATUS_FILTERS = [
  { key: "under_review", label: "Under Review" },
  { key: "more_info", label: "More Info Required" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "not_submitted", label: "Not Submitted" },
  { key: "all", label: "All" },
];

const STATUS_TO_QUERY = {
  under_review: "Under Review",
  more_info: "More Information Required",
  approved: "Approved",
  rejected: "Rejected",
  not_submitted: "Not Submitted",
  all: "all",
};

export default function AdminKycQueue() {
  const { user: authUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState("under_review");
  const [data, setData] = useState({ items: [], required_docs: [], counts: {} });
  const [activeId, setActiveId] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {url, filename, type, docId}
  const [redactDoc, setRedactDoc] = useState(null); // {docId, filename}
  const [workload, setWorkload] = useState(null);
  const [workloadChart, setWorkloadChart] = useState(null);
  const [team, setTeam] = useState([]);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [search, setSearch] = useState("");
  const [reviewerTemplates, setReviewerTemplates] = useState([]);
  const [tmplChoice, setTmplChoice] = useState("");
  const [visibilityFor, setVisibilityFor] = useState(null); // active row when opening the "List on Curated Deals" dialog
  const [bulkMoreInfoOpen, setBulkMoreInfoOpen] = useState(false);
  const [bulkTempApproveOpen, setBulkTempApproveOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [tempApproveConfirm, setTempApproveConfirm] = useState(false);
  const [quickApproveFor, setQuickApproveFor] = useState(null);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.items || [];
    return (data.items || []).filter((it) => {
      const hay = [it.startup_name, it.founder_name, it.founder_email,
                   it.legal_entity_name, it.cin, it.pan, it.gstin,
                   it.kyc_status, it.kyc_handoff_note].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [data.items, search]);

  const load = useCallback(async () => {
    try {
      const q = STATUS_TO_QUERY[filter] || "under_review";
      const url = `/admin/kyc-queue${q ? `?status=${encodeURIComponent(q)}` : ""}`;
      const [{ data }, wl, tm, wc] = await Promise.all([
        api.get(url),
        api.get("/admin/kyc/workload").catch(() => ({ data: null })),
        api.get("/admin/team").catch(() => ({ data: [] })),
        api.get("/admin/kyc/workload-chart?days=7").catch(() => ({ data: null })),
      ]);
      setData(data);
      setWorkload(wl?.data || null);
      setTeam(tm?.data || []);
      setWorkloadChart(wc?.data || null);
      // Deep-link from AI chat / bookmark: ?focus=<startup_id> pins that row.
      const focusId = searchParams.get("focus");
      if (focusId && data.items?.find((i) => i.startup_id === focusId)) {
        setActiveId(focusId);
        // Widen the filter automatically if the focused row is outside "under_review".
      } else if (data.items?.length && !data.items.find((i) => i.startup_id === activeId)) {
        setActiveId(data.items[0].startup_id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [filter, activeId, searchParams]);

  useEffect(() => { load(); }, [load]);

  // If a deep-link focus= is present when landing on this page, widen the
  // filter to "all" so the target row is reachable regardless of KYC status.
  useEffect(() => {
    if (searchParams.get("focus") && filter !== "all") setFilter("all");
  }, [searchParams, filter]);

  // Load KYC reviewer templates from admin settings so ops-editable
  // note patterns propagate to the startup queue too.
  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => {
        const t = r.data?.kyc?.reviewer_templates;
        if (Array.isArray(t)) setReviewerTemplates(t);
      })
      .catch(() => {});
  }, []);

  // Load KYC message thread whenever active startup changes.
  useEffect(() => {
    if (!activeId) { setThread([]); return; }
    api.get(`/admin/startups/${activeId}/kyc-thread`)
      .then((r) => setThread(r.data?.messages || []))
      .catch(() => setThread([]));
  }, [activeId]);

  const sendReviewerReply = async () => {
    if (!activeId || !reply.trim()) return;
    try {
      await api.post(`/admin/startups/${activeId}/kyc-reply`, { body: reply.trim() });
      const r = await api.get(`/admin/startups/${activeId}/kyc-thread`);
      setThread(r.data?.messages || []);
      setReply("");
      toast.success("Reply sent to founder.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reassign = async (startupId, userId, presetNote = null) => {
    try {
      // If we don't have a preset note + we're handing off to someone else,
      // open the modal to gather reviewer + note. Otherwise fall through.
      if (userId === undefined) {
        setHandoffTarget({ startupId, currentAssigneeId: null });
        return;
      }
      let handoff_note = presetNote;
      if (userId && handoff_note == null) {
        setHandoffTarget({ startupId, currentAssigneeId: userId });
        return;
      }
      await api.post(`/admin/startups/${startupId}/kyc-reassign`,
                     { user_id: userId || null, handoff_note });
      toast.success(userId ? "Reassigned." : "Cleared assignment.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [handoffTarget, setHandoffTarget] = useState(null);
  const confirmHandoff = async ({ user_id, handoff_note }) => {
    try {
      await api.post(`/admin/startups/${handoffTarget.startupId}/kyc-reassign`,
                     { user_id, handoff_note });
      toast.success("Reassigned.");
      setHandoffTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Bulk multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const bulkAction = async (action, opts = {}) => {
    if (selectedIds.size === 0) { toast.info("Nothing selected"); return; }
    if (action === "more_info" && !opts.note) {
      setBulkMoreInfoOpen(true);
      return;
    }
    if (action === "assign" && !opts.reviewer_id) {
      setBulkAssignOpen(true);
      return;
    }
    const body = { startup_ids: Array.from(selectedIds), action, ...opts };
    try {
      const res = await api.post("/admin/kyc/bulk", body);
      toast.success(`Updated ${res.data.updated} of ${selectedIds.size}.`);
      clearSelection();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmBulkMoreInfo = async ({ reason }) => {
    await bulkAction("more_info", { note: reason });
    setBulkMoreInfoOpen(false);
  };
  const confirmBulkAssign = async ({ user_id, handoff_note }) => {
    await bulkAction("assign", { reviewer_id: user_id, note: handoff_note });
    setBulkAssignOpen(false);
  };

  const bulkTempApprove = () => {
    if (selectedIds.size === 0) { toast.info("Nothing selected"); return; }
    setBulkTempApproveOpen(true);
  };
  const confirmBulkTempApprove = async ({ reason }) => {
    try {
      const res = await api.post("/admin/startups/bulk-temp-approve", {
        startup_ids: Array.from(selectedIds),
        note: reason,
      });
      toast.success(`Temp-approved ${res.data.modified} of ${selectedIds.size}.`);
      clearSelection();
      setBulkTempApproveOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const decide = async (status) => {
    if (!activeId) return;
    setBusy(true);
    try {
      await api.post(`/admin/startups/${activeId}/kyc-status`, { status, note: note || undefined });
      toast.success(`KYC marked ${status}.`);
      setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const tempApprove = () => {
    if (!activeId) return;
    setTempApproveConfirm(true);
  };
  const confirmTempApprove = async ({ reason }) => {
    if (!activeId) return;
    const label = active?.startup_name || activeId;
    setBusy(true);
    try {
      await api.post(`/admin/startups/${activeId}/temp-approve`, { note: reason || note || undefined });
      toast.success(`Temporarily approved · ${label}`);
      setNote("");
      setTempApproveConfirm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Row-level shortcut used by the "Approve now" pill next to rows whose
  // Sandbox recommendation is "approve" (identity + docs both green).
  const [quickApproveBusy, setQuickApproveBusy] = useState(null);
  const quickApprove = (row) => {
    if (!row?.startup_id) return;
    setQuickApproveFor(row);
  };
  const confirmQuickApprove = async () => {
    const row = quickApproveFor;
    if (!row) return;
    const label = row.startup_name || row.founder_email || "this startup";
    setQuickApproveBusy(row.startup_id);
    try {
      await api.post(`/admin/startups/${row.startup_id}/kyc-status`, {
        status: "Approved",
        note: "Auto-approved via Sandbox verification — founder identity + all required docs verified.",
      });
      toast.success(`Approved · ${label}`);
      setQuickApproveFor(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQuickApproveBusy(null);
    }
  };

  const openDoc = async (docId, disposition = "inline") => {
    const doc = active?.documents?.find((d) => d.doc_id === docId);
    try {
      const res = await api.get(`/admin/documents/${docId}/view`, {
        params: { disposition },
        responseType: "blob",
      });
      const mime = res.headers["content-type"] || doc?.content_type || "application/octet-stream";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      if (disposition === "attachment") {
        // Force download without opening the tab / modal.
        const a = document.createElement("a");
        a.href = url;
        a.download = doc?.filename || `document-${docId}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5_000);
      } else {
        // Preview inside the reviewer modal so context is preserved.
        setPreview({ url, filename: doc?.filename || `document-${docId}`, type: mime, docId });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const closePreview = () => {
    if (preview?.url) {
      try { URL.revokeObjectURL(preview.url); } catch { /* ignore */ }
    }
    setPreview(null);
  };

  const confirmVisibility = async ({ reason }) => {
    const row = visibilityFor;
    if (!row) return;
    try {
      await api.post(`/admin/startups/${row.startup_id}/marketplace-visibility`, { visible: true, reason });
      toast.success("Listed on Curated Deals");
      setVisibilityFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const active = data.items?.find((i) => i.startup_id === activeId) || null;
  const counts = data.counts || {};

  // Close the PDF preview when the reviewer hits ESC — feels native.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => { if (e.key === "Escape") closePreview(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  return (
    <DashboardShell title="KYC Queue" subtitle="ADMIN · KYC REVIEWER WORKFLOW">
      <Toaster richColors position="top-right" />
      {preview && <PdfPreviewModal preview={preview} onClose={closePreview} />}
      {redactDoc && (
        <PassportRedactor
          docId={redactDoc.docId}
          mode="document"
          title={`Founder Doc · Redactor · ${redactDoc.filename || redactDoc.docId}`}
          onClose={() => setRedactDoc(null)}
        />
      )}
      <HandoffModal
        open={!!handoffTarget}
        onClose={() => setHandoffTarget(null)}
        onConfirm={confirmHandoff}
        currentAssigneeId={handoffTarget?.currentAssigneeId}
      />
      <ConfirmReasonDialog
        open={!!visibilityFor}
        title={`List ${visibilityFor?.startup_name || "this startup"} on Curated Deals?`}
        description="Investors will see a KYC Pending badge until KYC is approved. Every force-listing is audit-logged."
        confirmLabel="List on Curated Deals"
        tone="gold"
        reasonPlaceholder="Why is this startup being featured now?"
        onConfirm={confirmVisibility}
        onClose={() => setVisibilityFor(null)}
        testId="kyc-visibility-dialog"
      />
      <ConfirmReasonDialog
        open={bulkMoreInfoOpen}
        title={`Send back ${selectedIds.size} startup${selectedIds.size === 1 ? "" : "s"} for more info?`}
        description="Every selected startup will move to 'More Information Required' and receive an email with this reason."
        confirmLabel="Send More-Info Request"
        tone="gold"
        reasonPlaceholder="e.g. Bank statement was for a different entity. Please re-upload."
        onConfirm={confirmBulkMoreInfo}
        onClose={() => setBulkMoreInfoOpen(false)}
        testId="kyc-bulk-more-info-dialog"
      />
      <ConfirmReasonDialog
        open={bulkTempApproveOpen}
        title={`Temporarily approve ${selectedIds.size} startup${selectedIds.size === 1 ? "" : "s"} without verified KYC?`}
        description="Every selected startup jumps to Approved and is marked 'admin_temp_approved'. They unlock the Verified Directory with an 'Approved by Admin · KYC Not Verified' badge until Sandbox verifications complete."
        confirmLabel="Temp-Approve Batch"
        tone="danger"
        reasonPlaceholder="Bulk provisional approval — Sandbox verifications to follow."
        onConfirm={confirmBulkTempApprove}
        onClose={() => setBulkTempApproveOpen(false)}
        testId="kyc-bulk-temp-approve-dialog"
      />
      <HandoffModal
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        onConfirm={confirmBulkAssign}
        currentAssigneeId={null}
      />
      <ConfirmReasonDialog
        open={tempApproveConfirm}
        title={`Temporarily approve ${active?.startup_name || activeId || "this startup"} without verified KYC?`}
        description="Flips KYC status to Approved and marks the startup 'admin_temp_approved'. The founder unlocks the Verified Directory but their passport will carry an 'Approved by Admin · KYC Not Verified' badge until Sandbox verifications complete."
        confirmLabel="Temp-Approve"
        tone="danger"
        reasonRequired={false}
        reasonLabel="Audit note (optional — falls back to the reviewer note above)"
        reasonPlaceholder="Why is this temp-approval necessary?"
        onConfirm={confirmTempApprove}
        onClose={() => setTempApproveConfirm(false)}
        testId="kyc-temp-approve-dialog"
      />
      <ConfirmReasonDialog
        open={!!quickApproveFor}
        title={`Approve ${quickApproveFor?.startup_name || quickApproveFor?.founder_email || "this startup"} now?`}
        description="Sandbox identity is verified and all required docs are complete. This closes the queue item and emails the founder."
        confirmLabel="Approve Now"
        tone="emerald"
        reasonRequired={false}
        reasonLabel="Optional reviewer note"
        reasonPlaceholder=""
        onConfirm={confirmQuickApprove}
        onClose={() => setQuickApproveFor(null)}
        testId="kyc-quick-approve-dialog"
      />

      {/* Workload / rebalance panel */}
      {workload && <WorkloadPanel workload={workload} onReassign={reassign} activeId={activeId} team={team} />}
      {workloadChart && <WorkloadChart chart={workloadChart} />}

      {/* Status filter tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="kyc-filter-tabs">
        {STATUS_FILTERS.map((f) => {
          const count = counts[f.key];
          const isActive = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-heading font-semibold transition-all ${
                isActive ? "bg-navy text-white" : "bg-white border border-navy/15 text-ink-soft hover:border-gold"
              }`}
              data-testid={`kyc-filter-${f.key}`}
            >
              {f.label}{typeof count === "number" ? ` · ${count}` : ""}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: applications list */}
        <div className="lg:col-span-1">
          <Card testId="kyc-list-card" className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-navy/10 bg-navy/[0.03] flex items-center justify-between">
              <div className="overline">Applications</div>
              <span className="text-[11px] font-mono text-ink-muted">
                {visibleItems.length}
                {visibleItems.length !== (data.items?.length || 0) ? ` / ${data.items?.length || 0}` : ""}
              </span>
            </div>
            <div className="px-3 py-2 border-b border-navy/10 bg-white">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, CIN, PAN, GSTIN…"
                className="w-full text-xs bg-transparent outline-none text-ink placeholder:text-ink-faint"
                data-testid="kyc-list-search"
              />
            </div>
            {selectedIds.size > 0 && (
              <div className="px-3 py-2 border-b border-emerald-200 bg-emerald-50 flex items-center flex-wrap gap-2" data-testid="kyc-bulk-toolbar">
                <span className="text-[11px] font-mono uppercase tracking-widest text-emerald-800">
                  {selectedIds.size} selected
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={() => bulkAction("approve")}
                    className="text-[11px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-heading"
                    data-testid="kyc-bulk-approve"
                  >Approve</button>
                  {authUser?.role === "super_admin" && (
                    <button
                      onClick={bulkTempApprove}
                      className="text-[11px] px-2 py-1 rounded border border-amber-500 bg-amber-100 hover:bg-amber-200 text-amber-900 font-heading inline-flex items-center gap-1"
                      data-testid="kyc-bulk-temp-approve"
                      title="Super-admin only — provisional approve (skip KYC) with a shared audit note"
                    >
                      <ShieldCheck size={11} /> Skip KYC
                    </button>
                  )}
                  <button
                    onClick={() => bulkAction("more_info")}
                    className="text-[11px] px-2 py-1 rounded border border-amber-400 text-amber-800 hover:bg-amber-50 font-heading"
                    data-testid="kyc-bulk-more-info"
                  >More info</button>
                  <button
                    onClick={() => bulkAction("assign")}
                    className="text-[11px] px-2 py-1 rounded border border-navy/20 text-ink hover:bg-navy/[0.04] font-heading"
                    data-testid="kyc-bulk-assign"
                  >Assign</button>
                  <button
                    onClick={clearSelection}
                    className="text-[11px] px-2 py-1 text-ink-muted hover:text-ink"
                    data-testid="kyc-bulk-clear"
                  >Clear</button>
                </div>
              </div>
            )}
            <div className="max-h-[70vh] overflow-y-auto">
              {visibleItems.length === 0 && (
                <div className="p-6 text-sm text-ink-muted italic" data-testid="kyc-empty">
                  {(data.items || []).length === 0 ? "No applications in this bucket." : "No applications match your search."}
                </div>
              )}
              {visibleItems.map((it) => {
                const isActive = it.startup_id === activeId;
                const isSelected = selectedIds.has(it.startup_id);
                const tone = it.kyc_status === "Approved" ? "success"
                  : it.kyc_status === "Rejected" ? "danger"
                  : it.kyc_status === "Under Review" ? "warn" : "muted";
                return (
                  <div
                    key={it.startup_id}
                    className={`w-full text-left border-b border-navy/[0.06] transition-colors flex ${
                      isActive ? "bg-gold/[0.10]" : "hover:bg-navy/[0.02]"
                    }`}
                    data-testid={`kyc-row-${it.startup_id}`}
                  >
                    <div className="pl-3 pt-4 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); toggleRow(it.startup_id); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                        data-testid={`kyc-select-${it.startup_id}`}
                        aria-label={`Select ${it.startup_name}`}
                      />
                    </div>
                    <div
                      onClick={() => { setActiveId(it.startup_id); setNote(""); }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveId(it.startup_id); setNote(""); } }}
                      className="flex-1 text-left px-3 py-3 cursor-pointer"
                    >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-heading text-ink truncate flex-1 pr-2">
                        {it.startup_name || it.founder_name || "—"}
                      </div>
                      <Pill tone={tone}>{it.kyc_status}</Pill>
                    </div>
                    <div className="text-[11px] font-mono text-ink-muted truncate">
                      {it.founder_email || "—"}
                    </div>
                    {it.kyc_handoff_note && (
                      <div className="mt-1.5 text-[11px] bg-amber-50 border border-amber-200 rounded p-1.5 text-amber-900 italic line-clamp-2" data-testid={`kyc-handoff-${it.startup_id}`}>
                        <span className="font-mono uppercase text-[9px] tracking-widest mr-1 not-italic">Handoff:</span>
                        {it.kyc_handoff_note}
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-2 text-[11px] flex-wrap">
                      <span className="inline-flex items-center gap-1 text-ink-muted">
                        <FileText size={11} /> {it.docs_uploaded} docs
                      </span>
                      {it.docs_missing.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-rose-700">
                          <AlertTriangle size={11} /> {it.docs_missing.length} missing
                        </span>
                      )}
                      {it.sandbox_identity_verified && (
                        <span
                          className="inline-flex items-center gap-1 text-emerald-700 font-heading bg-emerald-100/70 border border-emerald-200 rounded-full px-1.5 py-0.5"
                          title="Founder identity verified via Sandbox — DigiLocker + PAN"
                          data-testid={`kyc-row-idverified-${it.startup_id}`}
                        >
                          <CheckCircle2 size={11} /> ID Verified
                        </span>
                      )}
                      {it.sandbox_company_verified && (
                        <span
                          className="inline-flex items-center gap-1 text-emerald-700 font-heading bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5"
                          title="Company entity verified via Sandbox — CIN + GSTIN + Company PAN"
                          data-testid={`kyc-row-companyverified-${it.startup_id}`}
                        >
                          <CheckCircle2 size={11} /> Company ✓
                        </span>
                      )}
                      {it.sandbox_recommend_action === "approve" && it.kyc_status === "Under Review" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); quickApprove(it); }}
                          disabled={quickApproveBusy === it.startup_id}
                          className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-heading bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 ring-2 ring-emerald-300"
                          title="Sandbox verified · docs complete — one-tap approve without opening the drawer"
                          data-testid={`kyc-row-quick-approve-${it.startup_id}`}
                        >
                          {quickApproveBusy === it.startup_id ? "Approving…" : "▸ Approve now"}
                        </button>
                      )}
                      {it.sla_target_time && (
                        <KycSlaCountdown
                          targetTime={it.sla_target_time}
                          slaHours={it.sla_hours}
                          status={it.kyc_status}
                          testId={`kyc-sla-countdown-${it.startup_id}`}
                        />
                      )}
                      {it.kyc_submitted_at && !it.sla_breached && (
                        <span className="inline-flex items-center gap-1 text-ink-faint ml-auto">
                          <Clock size={11} /> {formatDateTime(it.kyc_submitted_at)}
                        </span>
                      )}
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Right: detail + action */}
        <div className="lg:col-span-2">
          {!active ? (
            <Card testId="kyc-empty-detail">
              <div className="py-12 text-center text-ink-muted">
                <ShieldCheck size={28} className="inline mb-3 text-gold" />
                <div className="text-sm">Select an application to review.</div>
              </div>
            </Card>
          ) : (
            <div className="space-y-6" data-testid="kyc-detail-pane">
              <Card testId="kyc-detail-header">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="overline flex items-center gap-2"><Building2 size={12} /> KYC Application</div>
                    <div className="font-heading text-2xl text-ink truncate mt-1">
                      {active.startup_name || active.founder_name || "—"}
                    </div>
                    <div className="text-xs font-mono text-ink-muted mt-1">
                      {active.founder_email} · CIN {active.cin || "—"} · Sector {active.sector || "—"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Pill tone={active.kyc_status === "Approved" ? "success" : active.kyc_status === "Rejected" ? "danger" : "warn"}>
                      {active.kyc_status}
                    </Pill>
                    <a
                      href={`/admin/kyc-snapshots/startup/${active.startup_id}`}
                      className="text-[11px] font-mono uppercase tracking-widest text-[#8A6600] hover:underline whitespace-nowrap"
                      data-testid={`kyc-snapshots-link-${active.startup_id}`}
                    >
                      Compare snapshots →
                    </a>
                    <button
                      onClick={async () => {
                        if (active.admin_force_visible) {
                          try {
                            await api.post(`/admin/startups/${active.startup_id}/marketplace-visibility`, { visible: false });
                            toast.success("Removed from Curated Deals");
                            load();
                          } catch (e) { toast.error("Toggle failed"); }
                        } else {
                          setVisibilityFor(active);
                        }
                      }}
                      className={`text-[11px] font-mono uppercase tracking-widest whitespace-nowrap hover:underline ${active.admin_force_visible ? "text-rose-700" : "text-emerald-700"}`}
                      data-testid={`kyc-detail-toggle-visibility-${active.startup_id}`}
                    >
                      {active.admin_force_visible ? "Hide from Curated Deals" : "List on Curated Deals →"}
                    </button>
                  </div>
                </div>
                {active.kyc_status === "Approved" && (
                  <div className="mb-3" data-testid="kyc-detail-approval-badge">
                    <AdminApprovalBadge entity={active} size="md" />
                  </div>
                )}
                {active.kyc_submitted_at && (
                  <div className="text-[11px] font-mono text-ink-muted flex items-center gap-2 flex-wrap">
                    <span>Submitted {formatDateTime(active.kyc_submitted_at)}</span>
                    {active.sla_target_time && active.kyc_status === "Under Review" && (
                      <KycSlaCountdown
                        targetTime={active.sla_target_time}
                        slaHours={active.sla_hours}
                        status={active.kyc_status}
                        testId={`kyc-detail-sla-${active.startup_id}`}
                      />
                    )}
                    {typeof active.hours_since_submit === "number" && active.kyc_status !== "Under Review" && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-heading uppercase tracking-widest bg-emerald-50 border border-emerald-300 text-emerald-700"
                      >
                        {active.hours_since_submit}h / {active.sla_hours}h · Resolved
                      </span>
                    )}
                  </div>
                )}
                {/* Reviewer selector — quick re-balance from the detail pane */}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Assigned Reviewer</span>
                  <select
                    value={active.assigned_poc_user_id || ""}
                    onChange={(e) => reassign(active.startup_id, e.target.value)}
                    className="text-xs bg-white border border-navy/15 rounded px-2 py-1 text-ink focus:border-gold outline-none"
                    data-testid={`kyc-reassign-${active.startup_id}`}
                  >
                    <option value="">— Unassigned —</option>
                    {team.map((t) => (
                      <option key={t.user_id} value={t.user_id}>
                        {t.name || t.email} · {t.role}
                      </option>
                    ))}
                  </select>
                </div>
                {active.kyc_note && (
                  <div className="mt-3 text-xs text-ink-soft border-l-2 border-gold/60 pl-3">
                    Latest note: {active.kyc_note}
                  </div>
                )}
                <Link
                  to={`/admin/company-intelligence/${active.startup_id}`}
                  className="mt-4 inline-flex items-center gap-1 text-[12px] font-heading text-[#8A6600] hover:underline"
                  data-testid="kyc-open-company-intel"
                >
                  Open Company Intelligence <ArrowRight size={13} />
                </Link>
              </Card>

              {/* Sandbox verifications — pass/fail rollup with per-check override */}
              <AdminSandboxKycCard userId={active.startup_id} testId="kyc-drawer-sandbox" />

              {/* Required docs checklist */}
              <Card testId="kyc-docs-checklist">
                <div className="overline mb-3">Required Documents</div>
                <ul className="space-y-2 text-sm">
                  {(data.required_docs || []).map((cat) => {
                    const uploaded = active.documents.find((d) => (d.category || "").toLowerCase() === cat);
                    return (
                      <li
                        key={cat}
                        className="flex items-center gap-2"
                        data-testid={`kyc-req-${cat}`}
                      >
                        {uploaded ? (
                          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                        ) : (
                          <XCircle size={16} className="text-rose-500 shrink-0" />
                        )}
                        <span className={uploaded ? "text-ink" : "text-ink-soft"}>
                          {humanize(cat)}
                        </span>
                        {uploaded && (
                          <>
                            <span className="ml-auto text-[11px] font-mono text-ink-muted truncate max-w-[180px]">
                              {uploaded.filename}
                            </span>
                            <button
                              onClick={() => openDoc(uploaded.doc_id, "inline")}
                              className="text-[11px] font-heading text-[#8A6600] hover:underline inline-flex items-center gap-1"
                              data-testid={`kyc-req-view-${cat}`}
                            >
                              <Eye size={11} /> View
                            </button>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>

              {/* All uploaded documents */}
              <Card testId="kyc-all-docs">
                <div className="overline mb-3">All Uploaded Documents ({active.documents.length})</div>
                {active.documents.length === 0 ? (
                  <div className="text-sm text-ink-muted italic">Founder hasn&apos;t uploaded any documents yet.</div>
                ) : (
                  <div className="divide-y divide-navy/[0.06]">
                    {active.documents.map((d) => (
                      <div key={d.doc_id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`kyc-doc-${d.doc_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-heading text-ink truncate">{d.filename}</div>
                          <div className="text-[11px] font-mono text-ink-muted mt-0.5">
                            {humanize(d.category)} · {d.size ? `${(d.size / 1024).toFixed(1)} KB` : "—"} · {d.uploaded_at ? formatDateTime(d.uploaded_at) : "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => openDoc(d.doc_id, "inline")}
                            className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
                            data-testid={`kyc-doc-view-${d.doc_id}`}
                            title="Open in new tab"
                          >
                            <Eye size={12} /> View
                          </button>
                          <button
                            onClick={() => openDoc(d.doc_id, "attachment")}
                            className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
                            data-testid={`kyc-doc-download-${d.doc_id}`}
                            title="Download"
                          >
                            <Download size={12} />
                          </button>
                          {(d.filename || "").toLowerCase().endsWith(".pdf") && (
                            <button
                              onClick={() => setRedactDoc({ docId: d.doc_id, filename: d.filename })}
                              className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-emerald-500 hover:text-emerald-800 inline-flex items-center gap-1"
                              data-testid={`kyc-doc-redact-${d.doc_id}`}
                              title="Redact founder PII before sharing with investors"
                            >
                              <EyeOff size={12} /> Redact
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* KYC message thread — founder ⇄ reviewer */}
              <KycThreadCard
                thread={thread}
                reply={reply}
                setReply={setReply}
                onSend={sendReviewerReply}
              />

              {/* Decision panel */}
              <Card testId="kyc-decision-card">
                {(active.sandbox_identity_verified || active.sandbox_company_verified) && (
                  <div
                    className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 flex items-start gap-2"
                    data-testid="kyc-decision-sandbox-banner"
                  >
                    <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1 text-xs">
                      <div className="font-heading text-emerald-800 text-sm">
                        {active.sandbox_identity_verified && active.sandbox_company_verified
                          ? "Founder ID & Company verified via Sandbox"
                          : active.sandbox_identity_verified
                            ? "Founder ID verified via Sandbox"
                            : "Company verified via Sandbox"}
                      </div>
                      <div className="text-emerald-700/80 mt-0.5">
                        {active.sandbox_recommend_action === "approve"
                          ? "Recommended: Approve — all Sandbox checks green and required docs are complete."
                          : "Identity is auto-verified. Please review the uploaded documents before approving."}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="overline flex items-center gap-2"><ShieldCheck size={14} /> Reviewer Decision</div>
                  {reviewerTemplates.length > 0 && (
                    <select
                      value={tmplChoice}
                      onChange={(e) => {
                        setTmplChoice(e.target.value);
                        const t = reviewerTemplates[parseInt(e.target.value, 10)];
                        if (t) setNote(t.note);
                      }}
                      className="text-xs bg-white border border-navy/15 rounded-md px-2 py-1 font-heading"
                      data-testid="kyc-startup-template"
                      aria-label="Apply reviewer note template"
                    >
                      <option value="">Templates…</option>
                      {reviewerTemplates.map((t, i) => (
                        <option key={t.label} value={i}>{t.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <TextArea
                  label="Note (visible in email to founder + audit log)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Bank statement was for a different entity. Please re-upload with account name matching the CIN."
                  testId="kyc-decision-note"
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
                  <Button
                    variant="gold"
                    onClick={() => decide("Approved")}
                    disabled={busy}
                    data-testid="kyc-btn-approve"
                    className={active.sandbox_recommend_action === "approve" ? "ring-2 ring-emerald-400 ring-offset-1" : ""}
                    title={active.sandbox_recommend_action === "approve" ? "Recommended · Sandbox identity + docs complete" : undefined}
                  >
                    <CheckCircle2 size={14} className="inline mr-1.5" />
                    {active.sandbox_recommend_action === "approve" ? "Approve · recommended" : "Approve"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => decide("More Information Required")}
                    disabled={busy}
                    data-testid="kyc-btn-more-info"
                  >
                    <MailQuestion size={14} className="inline mr-1.5" /> More Info
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => decide("Rejected")}
                    disabled={busy}
                    data-testid="kyc-btn-reject"
                  >
                    <span className="text-rose-700 inline-flex items-center">
                      <XCircle size={14} className="inline mr-1.5" /> Reject
                    </span>
                  </Button>
                </div>
                {active.kyc_status !== "Approved" && (
                  <div className="mt-3 pt-3 border-t border-dashed border-amber-300">
                    <Button
                      variant="ghost"
                      onClick={tempApprove}
                      disabled={busy}
                      data-testid="kyc-btn-temp-approve"
                      className="w-full !border-amber-400 hover:!bg-amber-50"
                    >
                      <span className="text-amber-800 inline-flex items-center">
                        <ShieldCheck size={14} className="inline mr-1.5" />
                        Approve · Skip KYC (temporary)
                      </span>
                    </Button>
                    <div className="text-[11px] text-amber-800/80 mt-1.5">
                      Unlocks the founder immediately. Passport carries an&nbsp;
                      <span className="font-heading">&ldquo;Approved by Admin · KYC Not Verified&rdquo;</span>&nbsp;
                      badge combo until Sandbox verifications complete.
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-ink-muted mt-3">
                  The founder is emailed a status change with your note. Adjust required document
                  categories in <Link to="/admin/settings" className="text-[#8A6600] hover:underline">Settings → KYC</Link>.
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
